import { ReactNode, Fragment, CSSProperties } from "react";
import dayjs from "dayjs";
import millify from "millify";
import { GraphTooltip } from "pages/DashboardPage/components/Graphs";
import {
    Area,
    AreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Text,
    Tooltip,
    XAxis,
    YAxis,
    Legend,
    ReferenceLine as ReferenceLineComponent,
    ReferenceDot
} from "recharts";
import Icon from "./Icon";
import Sty from "modules/Sty";
import TagSymphony from "./TagSymphony";
import useScreen from "Hooks/useScreen";
import { formatNumberToDisplayType } from "modules/Utils";
import Colors from "modules/Colors";

export interface AreaChartDataSet {
    key: string;
    color: string;
    title?: string;
    subtitle?: string;
    hideFromGraph?: boolean;
    summationSection?: boolean;
}

const Tick = (props: any) => {
    const { payload: { value } } = props;
    const color = value === "yourdata" ? "red" : "#666";

    return (
        <Text
            {...props}
            fill={color}
            style={{
                zIndex: 99
            }}
        >
            {millify(value, { precision: 2 })}
        </Text>
    )
}

const formatCamelCase = (str: string): string => {
    return str.replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
};

/** Configuration for a vertical reference line on the chart */
export interface AreaChartSymphonyReferenceLine {
    /** Title to display on the reference line */
    title: string;
    /** Date where the line should appear (format: YYYY-MM-DD) */
    date: string;
    /** Optional color for the line and label */
    color?: string;
}

/** 
 * Intelligently assign the year to a date string based on the month/day
 * to handle year boundaries correctly 
 */
const getSmartYear = (dateStr: string) => {
    // If we have a full date with year already, just return it
    if (dayjs(dateStr, 'YYYY-MM-DD').isValid()) {
        return dayjs(dateStr);
    }
    
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth(); // 0-11
    
    // Parse the month from date (assuming MM/DD format)
    const parts = dateStr.split('/');
    if (parts.length < 2) return dayjs(dateStr).year(currentYear);
    
    const month = parseInt(parts[0], 10) - 1; // Convert to 0-11 format
    
    // If we're currently in Jan/Feb and the date is Nov/Dec, 
    // it's likely from the previous year
    if (currentMonth < 2 && month > 9) {
        return dayjs(dateStr).year(currentYear - 1);
    }
    
    // If we're currently in Nov/Dec and the date is Jan/Feb,
    // it's likely from the next year
    if (currentMonth > 9 && month < 2) {
        return dayjs(dateStr).year(currentYear + 1);
    }
    
    // Otherwise use current year
    return dayjs(dateStr).year(currentYear);
};

interface AreaChartSymphonyProps {
    className?: string;
    data: {
        date: string;
        value: number;
    }[]
    width?: number;
    height?: number;
    xTickHeight?: number;
    yTickWidth?: number;
    showLegend?: boolean;
    startDate?: string;
    endDate?: string;
    yAxisName?: string;

    title?: string;
    icon?: string;
    currentValue?: number;
    percentageChange?: number;
    rightSideTitle?: string;
    rightSideValue?: number;
    /** Optional array of reference lines to display */
    referenceLines?: AreaChartSymphonyReferenceLine[];
}

/** Custom label component for reference lines */
const CustomReferenceLineLabel = ({
    viewBox,
    title,
    value,
    date,
    position = 'end',
    index = 0,
    totalLabels = 1
}: {
    viewBox: { x: number; y: number };
    title: string;
    value: number;
    date: string;
    position?: 'start' | 'end';
    index: number;
    totalLabels: number;
}) => {
    const { x } = viewBox;

    // Calculate position to avoid overlapping
    const xOffset = position === 'start' ? -10 : 10;
    const yOffset = 40 + (index * 40); // Increased spacing for date value

    return (
        <g>
            <text
                x={x + xOffset}
                y={yOffset}
                fontSize={16}
                fill="#000000"
                className="font-semibold"
                textAnchor={position === 'start' ? 'end' : 'start'}
            >
                {title}
            </text>
            <text
                x={x + xOffset}
                y={yOffset + 25}
                fontSize={15}
                fill="#666666"
                textAnchor={position === 'start' ? 'end' : 'start'}
            >
                {getSmartYear(date).format('MM/DD')}
            </text>
        </g>
    );
};

const AreaChartSymphony = ({
    className,
    data,
    width = 500,
    height = 300,
    yAxisName,
    xTickHeight = 20,
    yTickWidth = 8,
    showLegend = false,
    title,
    icon,
    currentValue,
    percentageChange,
    rightSideTitle,
    rightSideValue,
    referenceLines,
}: AreaChartSymphonyProps) => {
    const { mobileView, mStyle } = useScreen();

    const lineChartSvgId = `${Date.now()}-splitColor`

    const colors = [Colors.purple, Colors.blue]; // Default and complementary color

    const renderAreas = () => {
        return (
            <Area
                type="monotone"
                dataKey="value"
                strokeWidth={2}
                stroke={colors[0]}
                fill={`url(#${lineChartSvgId}-0)`}
                activeDot={{ r: 8 }}
            />
        );
    };

    const renderPercentageChange = () => {
        if (!percentageChange) {
            return null;
        }

        const isDecrease = percentageChange < 0;
        const value = formatNumberToDisplayType(percentageChange, 'percentage');

        return (
            <TagSymphony
                className="areaChartHeaderPercentageChange"
                title={value ? `${isDecrease ? '' : '+'}${value}` : ''}
                color={isDecrease ? "grey" : "green"}
                type="square"
                style={{
                    fontSize: 22,
                    fontWeight: 600,
                }}
            />
        )
    }


    const renderAreaChartHeader = () => {
        if (!title || !currentValue) {
            return null;
        }

        let formattedCurrentValue = formatNumberToDisplayType(currentValue, 'number');

        return (
            <div className="areaChartHeader" style={mStyle(sty, 'areaChartHeader')}>
                {icon &&
                    <Icon
                        className="areaChartHeaderIcon"
                        icon={icon}
                        size={40}
                    />
                }
                <div className="areaChartHeaderInfo">
                    <div className="areaChartHeaderTitle" style={mobileView ? Sty.text14grey : Sty.text16grey}>
                        {title}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div
                            className="areaChartHeaderCurrentValue"
                            style={mobileView ?
                                Sty.text20
                                :
                                Sty.text32
                            }
                        >
                            {formattedCurrentValue}
                        </div>

                    </div>
                </div>
                <div className="areaChartRightSide" style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
                    {percentageChange &&
                        renderPercentageChange()
                    }
                    {/*                     
                    <div className="areaChartRightSideTitle" style={mobileView ? Sty.text14grey : Sty.text16grey}>
                        {rightSideTitle}
                    </div>
                    <div className="areaChartRightSideValue" style={mobileView ? Sty.text16 : Sty.text20}>
                        {rightSideValue ? `$${millify(rightSideValue, { precision: 2 })}` : '-'}
                    </div> */}
                </div>
            </div>
        )
    }

    /** Renders reference lines if provided */
    const renderReferenceLines = () => {
        if (!referenceLines?.length) return null;

        return referenceLines.map((ref, index) => {
            const matchingDataPoint = data.find(point =>
                dayjs(point.date).format('YYYY-MM-DD') === dayjs(ref.date).format('YYYY-MM-DD')
            );

            if (!matchingDataPoint) return null;

            return (
                <Fragment key={`ref-line-${ref.date}-${index}`}>
                    <ReferenceLineComponent
                        x={matchingDataPoint.date}
                        stroke={ref.color || '#42c221'}
                        strokeWidth={2}
                        strokeDasharray="3 4"
                        label={({ viewBox }) => (
                            <CustomReferenceLineLabel
                                viewBox={viewBox}
                                title={ref.title}
                                value={matchingDataPoint.value}
                                date={ref.date}
                                position={index === referenceLines.length - 1 ? 'start' : 'end'}
                                index={index}
                                totalLabels={referenceLines.length}
                            />
                        )}
                        isFront={true}
                    />
                    {/* Add the reference dot separately */}
                    <ReferenceDot
                        x={matchingDataPoint.date}
                        y={matchingDataPoint.value}
                        r={10}
                        fill={ref.color || '#42c221'}
                        stroke="white"
                        strokeWidth={2}
                        isFront={true}
                    />
                </Fragment>
            );
        });
    };

    return (
        <div className="areaChartElement" style={sty.areaChartElement}>
            {renderAreaChartHeader()}

            <ResponsiveContainer
                className={`areaChartResponsiveContainer ${className} mx-auto`}
                width="100%"
                height={window.innerWidth < 576 ? 250 : window.innerHeight * 0.3}
                minHeight="100px"
                style={{ paddingLeft: 16 }}
            >
                <AreaChart
                    width={width}
                    height={height}
                    data={data}
                    margin={{
                        top: 15,
                        right: 0,
                        left: window.innerWidth >= 768 ? 20 : 0,
                        bottom: 5,
                    }}
                    onClick={(data) => {
                        if (data) {
                            console.log('Clicked point:', data);
                        }
                    }}
                >
                    <CartesianGrid
                        stroke="#8800FF29"
                        strokeWidth={1.5}
                        strokeDasharray="8 8"
                    />
                    <XAxis
                        dy={12}
                        axisLine={false}
                        dataKey={"date"}
                        tickLine={false}
                        minTickGap={window.innerWidth < 768 ? 20 : 30}
                        width={width}
                        height={xTickHeight}
                        tickFormatter={(value: any, index: number) => {
                            const date = dayjs(value).format("MM/DD")
                            return date !== 'Invalid Date' ? date : value
                        }}
                    />
                    <YAxis
                        mirror={window.innerWidth < 768}
                        height={height}
                        width={yTickWidth}
                        tick={<Tick />}
                        tickCount={window.innerWidth < 768 ? 3 : 4}
                        axisLine={false}
                        minTickGap={window.innerWidth < 768 ? 30 : 20}
                        tickLine={false}
                        allowDecimals={false}
                        domain={[(min: any) => Math.floor(min * 0.99), (max: any) => Math.ceil(max * 1.01)]}
                    />
                    <Tooltip
                        cursor={{ stroke: Colors.purple, strokeWidth: 2 }}
                        content={({ active, payload, label }) => {
                            if (active && payload && payload.length) {
                                const regularItems: ReactNode[] = [];
                                const summationItems: ReactNode[] = [];
                                regularItems.push(
                                    <Fragment key="single">
                                        <strong style={{ fontWeight: 600 }}>{formatCamelCase(yAxisName || '')}</strong>:{' '}
                                        {payload[0]?.value?.toLocaleString('en')}
                                    </Fragment>
                                );

                                return (
                                    <GraphTooltip
                                        title={getSmartYear(label).format('ddd MM/DD')}
                                        value={
                                            <div className="areaChartTooltipValue">
                                                {regularItems}
                                                {summationItems.length > 0 &&
                                                    <>
                                                        <hr style={{ margin: '8px 0', borderTop: '1px solid #ccc' }} />
                                                        {summationItems}
                                                    </>
                                                }
                                            </div>
                                        }
                                    />
                                );
                            }
                            return null;
                        }}
                    />
                    {showLegend && <Legend wrapperStyle={{ bottom: -20 }} verticalAlign="bottom" align="center" formatter={(value) => formatCamelCase(value)} />}
                    <defs key="gradient-0">
                        <linearGradient
                            id={`${lineChartSvgId}-0`}
                            x1="0" y1="0" x2="0" y2="1"
                        >
                            <stop stopColor={colors[0]} stopOpacity={0.4} />
                            <stop offset="1" stopColor={colors[0]} stopOpacity={0.05} />
                        </linearGradient>
                    </defs>
                    {renderAreas()}
                    {renderReferenceLines()}
                </AreaChart>
            </ResponsiveContainer>
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    areaChartElement: {
        position: 'relative'
    },
    areaChartHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: 16,
    },
    areaChartHeader_mobile: {
        gap: 8,
    },
    areaChartHeaderPercentageChange: {
    }
}

export default AreaChartSymphony;